import { useEffect, useState } from 'react';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { displayToastSuccess } from '@/helpers/display-toast';
import { usePermissions } from '@/modules/common';
import { FeatureMode } from '@/modules/common/components/templates/feature-details/types/types';
import { useApiError } from '@/modules/common/hooks/use-api-error';
import { useFeatureTabNavigation } from '@/modules/common/hooks/use-feature-tab-navigation';
import { OpsiumPolicy } from '@/modules/common/types/opsium-policy';
import { PatchEntityResult } from '@/modules/common/types/patch-entity-result';
import { invalidateProjectQueries } from '@/modules/common/utils/query-invalidation';
import {
  createProject,
  getProjectDetails,
  updateProject,
  updateProjectIntegrations,
  uploadProjectLogo,
} from '@/modules/projects';
import { ProjectFormData } from '@/modules/projects/types';

import { ProjectTab } from '../types';

interface ProjectSaveParams {
  data: ProjectFormData | (Partial<ProjectFormData> & { id: string });
  successCallback?: (updatedProjectResult: PatchEntityResult) => void;
  errorCallback?: (error: unknown) => void;
}

interface LogoUploadParams {
  projectId: string;
  file: File;
}

/**
 * Manages project details state and operations for both create and edit modes.
 * Handles project CRUD operations, tab navigation, and file upload functionality.
 *
 * Features:
 * - Project creation and update with optimistic UI updates
 * - Tab-based navigation for multi-step project forms
 * - Logo upload with cache invalidation
 * - Mode management (Create/Edit/Read) based on user permissions
 * - Integrated error handling with user-friendly toast notifications
 */
export function useProjectDetails() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t } = useTranslation(['authenticated.projects']);
  const { displayToastErrorFromApiWithFallback } = useApiError();

  const { hasPermission } = usePermissions();
  const isEditProjectEnabled = hasPermission(OpsiumPolicy.ProjectsEdit);

  const defaultMode =
    projectId === 'new'
      ? FeatureMode.Create
      : isEditProjectEnabled
        ? FeatureMode.Edit
        : FeatureMode.Read;

  const [mode, setMode] = useState<FeatureMode>(defaultMode);
  const [onSaveSuccess, setOnSaveSuccess] = useState<((result: PatchEntityResult) => void) | null>(
    null
  );

  // Update mode when permissions load or change (handles page refresh scenario)
  useEffect(() => {
    // Only update if we're not in create mode
    if (projectId !== 'new') {
      const newMode = isEditProjectEnabled ? FeatureMode.Edit : FeatureMode.Read;
      setMode(newMode);
    }
  }, [projectId, isEditProjectEnabled]);

  const { currentTab, setCurrentTab, navigateToTab } = useFeatureTabNavigation({
    mode,
    defaultTab: ProjectTab.Info,
    validTabs: Object.values(ProjectTab),
    entityId: projectId,
  });

  const {
    data: projectDetailsResult,
    isLoading: isLoadingProject,
    error: projectError,
  } = useQuery({
    queryKey: ['project', projectId],
    queryFn: async () => {
      const id = parseInt(projectId!, 10);
      return await getProjectDetails(id);
    },
    enabled: Boolean(projectId && projectId !== 'new'),
  });

  // Extract project data and raw integrations from result
  const projectData = projectDetailsResult?.project;
  const rawIntegrations = projectDetailsResult?.rawIntegrations;

  const createProjectMutation = useMutation({
    mutationFn: async (data: ProjectFormData) => {
      // Step 1: Create the project
      const projectId = await createProject(data);

      // Step 2: Upload logo if provided
      if (data.logo instanceof File) {
        await uploadProjectLogo({ projectId: projectId.toString(), file: data.logo });
      }

      return projectId;
    },
    onSuccess: async () => {
      await invalidateProjectQueries(queryClient);
      displayToastSuccess(t('toasts.projectCreatedSuccess'));

      // Call success callback before navigation (allows navigation guard to be bypassed)
      if (onSaveSuccess) {
        onSaveSuccess({ patchedArchivedRanges: [] });
        setOnSaveSuccess(null);
      }

      navigate(`/projects`, { replace: true });
    },
    onError: error => {
      displayToastErrorFromApiWithFallback(
        error,
        'authenticated.projects:toasts.createProjectError'
      );
    },
  });

  const updateProjectMutation = useMutation({
    mutationFn: async (data: Partial<ProjectFormData> & { id: string }) => {
      const { id, effectiveStartDate, effectiveEndDate, ...updateData } = data;
      const numericId = parseInt(id, 10);

      // Use status from form data if present, otherwise fall back to cached project data
      const currentStatus = updateData.status ?? projectData?.info.status;

      // Extract effectiveStartDate - if it's a string (YYYY-MM-DD), convert to Date
      // If not provided, fall back to current date
      let startDate: Date;
      if (effectiveStartDate) {
        if (typeof effectiveStartDate === 'string') {
          // Parse YYYY-MM-DD format to Date at UTC midnight
          const [year, month, day] = effectiveStartDate.split('-').map(Number);
          startDate = new Date(Date.UTC(year, month - 1, day, 0, 0, 0, 0));
        } else {
          startDate = effectiveStartDate;
        }
      } else {
        startDate = new Date();
      }

      // Extract effectiveEndDate - if it's a string (YYYY-MM-DD), convert to Date
      // Can be null for infinite/ongoing changes
      let endDate: Date | null | undefined;
      if (effectiveEndDate !== undefined) {
        if (effectiveEndDate === null) {
          endDate = null; // Infinite/ongoing
        } else if (typeof effectiveEndDate === 'string') {
          // Parse YYYY-MM-DD format to Date at UTC midnight
          const [year, month, day] = effectiveEndDate.split('-').map(Number);
          endDate = new Date(Date.UTC(year, month - 1, day, 0, 0, 0, 0));
        } else {
          endDate = effectiveEndDate;
        }
      } else {
        endDate = undefined; // Not provided
      }

      const result = await updateProject(numericId, updateData, startDate, currentStatus, endDate);
      const updatedProjectResult = await getProjectDetails(numericId);
      await invalidateProjectQueries(queryClient);
      queryClient.setQueryData(
        ['project', updatedProjectResult.project.id.toString()],
        updatedProjectResult
      );

      return result;
    },
    onSuccess: async updatedProjectResult => {
      displayToastSuccess(t('toasts.projectUpdatedSuccess'));
      if (onSaveSuccess) {
        onSaveSuccess(updatedProjectResult);
        setOnSaveSuccess(null);
      }
    },
    onError: error => {
      displayToastErrorFromApiWithFallback(
        error,
        'authenticated.projects:toasts.updateProjectError'
      );
    },
  });

  const updateProjectIntegrationsMutation = useMutation({
    mutationFn: async (data: Partial<ProjectFormData> & { id: string }) => {
      const { id, integrations } = data;
      const numericId = parseInt(id, 10);

      if (integrations === undefined) {
        return await getProjectDetails(numericId);
      }

      await updateProjectIntegrations(numericId, integrations);
      return await getProjectDetails(numericId);
    },
    onSuccess: async updatedProjectResult => {
      queryClient.setQueryData(
        ['project', updatedProjectResult.project.id.toString()],
        updatedProjectResult
      );
      await invalidateProjectQueries(queryClient);
      // Invalidate available integrations to refetch and update the form state
      queryClient.invalidateQueries({
        queryKey: ['available-integrations', mode, updatedProjectResult.project.id],
      });
      displayToastSuccess(t('toasts.projectUpdatedSuccess'));

      if (onSaveSuccess) {
        onSaveSuccess({ patchedArchivedRanges: [] });
        setOnSaveSuccess(null);
      }
    },
    onError: error => {
      displayToastErrorFromApiWithFallback(
        error,
        'authenticated.projects:toasts.updateProjectError'
      );
    },
  });

  const uploadLogoMutation = useMutation({
    mutationFn: ({ projectId, file }: LogoUploadParams) => uploadProjectLogo({ projectId, file }),
    onSuccess: async () => {
      await invalidateProjectQueries(queryClient);
      displayToastSuccess(t('toasts.logoUploadedSuccess'));
    },
    onError: error => {
      displayToastErrorFromApiWithFallback(error, 'authenticated.projects:toasts.logoUploadError');
    },
  });

  const handleSaveProject = ({ data, successCallback, errorCallback }: ProjectSaveParams) => {
    // Store the callback function in state (wrap in arrow function to prevent React from executing it)
    setOnSaveSuccess(() => (result: PatchEntityResult) => {
      if (successCallback) {
        successCallback(result);
      }
    });

    if (mode === FeatureMode.Create) {
      createProjectMutation.mutate(data as ProjectFormData, { onError: errorCallback });
    } else {
      // Use integrations-specific endpoint when on integrations tab
      if (currentTab === ProjectTab.Integrations) {
        updateProjectIntegrationsMutation.mutate(
          { ...data, id: projectId! } as Partial<ProjectFormData> & { id: string },
          { onError: errorCallback }
        );
      } else {
        updateProjectMutation.mutate(
          { ...data, id: projectId! } as Partial<ProjectFormData> & { id: string },
          { onError: errorCallback }
        );
      }
    }
  };

  const handleTabChange = (tab: ProjectTab | string, additionalParams?: Record<string, string>) => {
    const targetTab = typeof tab === 'string' ? (tab as ProjectTab) : tab;

    if (additionalParams) {
      // Use navigateToTab when additional params are provided
      navigateToTab(targetTab, additionalParams);
    } else {
      // Simple tab change without additional params
      setCurrentTab(targetTab);
    }
  };

  const handleLogoUpload = (file: File) => {
    if (projectId && projectId !== 'new') {
      uploadLogoMutation.mutate({ projectId, file });
    }
  };

  const isLoading =
    createProjectMutation.isPending ||
    updateProjectMutation.isPending ||
    updateProjectIntegrationsMutation.isPending;

  return {
    // Data
    projectData,
    rawIntegrations,
    mode,
    currentTab,

    // Loading states
    isLoading,
    isLoadingProject,
    isUploadingLogo: uploadLogoMutation.isPending,

    // Error states - only return project loading errors, not form submission errors
    error: projectError,

    // Actions
    handleSaveProject,
    handleTabChange,
    handleLogoUpload,
    navigateToTab,
  };
}
